import React, { useEffect, useRef, useState, useCallback } from 'react';

// Only one dashboard tab may be active per browser. Multiple tabs fight over
// the shared JWT/CSRF cookies (each tab's token refresh invalidates the
// others' in-flight requests) and multiply device websocket connections.
// The newest tab claims the session; older tabs unmount the app — closing
// their websockets and timers — and show a takeover screen.
const CHANNEL_NAME = 'devkit-dashboard-active-tab';

export default function SingleTabGuard({ children }) {
  const tabIdRef = useRef(
    (window.crypto?.randomUUID && window.crypto.randomUUID()) ||
      `${Math.random()}-${performance.now()}`
  );
  const channelRef = useRef(null);
  const [blocked, setBlocked] = useState(false);

  const claim = useCallback(() => {
    channelRef.current?.postMessage({ type: 'claim', id: tabIdRef.current });
    setBlocked(false);
  }, []);

  useEffect(() => {
    if (typeof BroadcastChannel === 'undefined') return undefined; // very old browser: no guard
    // Never claim (or yield) from an embedded copy of the app: if a
    // mis-routed same-origin iframe (e.g. an SPA fallback serving
    // index.html) boots the dashboard, it must not hijack the real tab's
    // session — that unmounts the app and closes its popups.
    if (window.self !== window.top) return undefined;

    const channel = new BroadcastChannel(CHANNEL_NAME);
    channelRef.current = channel;

    channel.onmessage = (e) => {
      if (e.data?.type === 'claim' && e.data.id !== tabIdRef.current) {
        setBlocked(true);
      }
    };

    channel.postMessage({ type: 'claim', id: tabIdRef.current });

    return () => {
      channel.close();
      channelRef.current = null;
    };
  }, []);

  if (!blocked) return children;

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '1rem',
        width: '100%',
        height: '100vh',
        backgroundColor: '#000',
        color: '#9ca3af',
        fontFamily: 'sans-serif',
        textAlign: 'center',
        padding: '0 2rem',
      }}
    >
      <img
        src={`${window.location.origin}/assets/tocaroLogo.webp`}
        alt="Tocaro Blue"
        style={{ height: '48px', width: 'auto', userSelect: 'none' }}
        draggable={false}
      />
      <h2 style={{ color: '#4b5563', fontWeight: 500, margin: 0 }}>
        DevKit Dashboard is open in another tab
      </h2>
      <p style={{ margin: 0, fontSize: '14px' }}>
        Only one dashboard tab can be active at a time.
      </p>
      <button
        onClick={claim}
        style={{
          background: '#222',
          color: '#fff',
          border: '1px solid #444',
          borderRadius: 4,
          padding: '8px 20px',
          cursor: 'pointer',
        }}
      >
        Use here instead
      </button>
    </div>
  );
}
